import React, { useMemo, useState } from 'react';
import {
  Autocomplete, Badge, Box, Button, Chip, Collapse, InputAdornment, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import { GROUP_COLORS, TYPE_OPTIONS } from './constants';
import { FilterLabelKey, countActiveFilters, filterLabels, hasAnyFilter, isoDay } from './format';
import ProductFilter from './ProductFilter';
import SavedViewsMenu from './SavedViewsMenu';
import { DocumentFilters, DocumentsMeta, Facets, PharmacyOption } from './types';

interface Props {
  meta: DocumentsMeta;
  filters: DocumentFilters;
  facets: Facets | null;
  patch: (patch: Partial<DocumentFilters>) => void;
  replace: (filters: DocumentFilters) => void;
  reset: () => void;
}

const BORDER = '#e2e8f0';
const fieldSx = { '& .MuiInputBase-root': { bgcolor: '#fff' } };

const DocumentsFilters: React.FC<Props> = ({ meta, filters, facets, patch, replace, reset }) => {
  const [advanced, setAdvanced] = useState(false);

  const pharmacyByGuid = useMemo(() => new Map(meta.pharmacies.map(p => [p.guid, p])), [meta.pharmacies]);
  const toOptions = (guids: string[]) => guids.map(g => pharmacyByGuid.get(g)).filter(Boolean) as PharmacyOption[];

  const groupCount = (statuses: number[]) =>
    facets ? statuses.reduce((sum, id) => sum + (facets.status[id] || 0), 0) : null;

  const toggleGroup = (id: string) => {
    const has = filters.statuses.includes(id);
    patch({ statuses: has ? filters.statuses.filter(s => s !== id) : [...filters.statuses, id] });
  };

  const numericStatuses = filters.statuses.filter(s => /^\d+$/.test(s));
  const groupTokens = filters.statuses.filter(s => !/^\d+$/.test(s));

  const statusOptions = useMemo(() => meta.statuses.map(s => {
    const group = meta.groups.find(g => g.statuses.includes(s.id));
    return { id: String(s.id), name: s.name, group: group ? group.name : 'Прочие' };
  }), [meta.statuses, meta.groups]);

  const created = { from: filters.createdFrom, to: filters.createdTo };
  const rangePreset = (days: number) => {
    const from = isoDay(-days);
    const to = isoDay(0);
    return created.from === from && created.to === to;
  };
  const applyRange = (days: number) => {
    if (rangePreset(days)) patch({ createdFrom: '', createdTo: '' });
    else patch({ createdFrom: isoDay(-days), createdTo: isoDay(0) });
  };

  const activeCount = countActiveFilters(filters);

  // Чипы активных фильтров с крестиком (поиск, тип, группы статусов и «Зависшие» уже видны в основной строке)
  const clearers: Partial<Record<FilterLabelKey, () => void>> = {
    pharmacy: () => patch({ pharmacy: [] }),
    sender: () => patch({ sender: [] }),
    recipient: () => patch({ recipient: [] }),
    region: () => patch({ region: [] }),
    status: () => patch({ statuses: groupTokens }),
    route: () => patch({ route: '' }),
    marking: () => patch({ marking: '' }),
    created: () => patch({ createdFrom: '', createdTo: '' }),
    updated: () => patch({ updatedFrom: '', updatedTo: '' }),
    stuck: () => patch({ stuckDays: '' }),
    hidden: () => patch({ hidden: 'all' }),
    author: () => patch({ author: '' }),
    product: () => patch({ product: '', products: [] })
  };
  const activeChips = filterLabels(filters, meta)
    .filter(c => clearers[c.key])
    .map(c => ({ ...c, clear: clearers[c.key] as () => void }));

  const pharmacyAutocomplete = (
    label: string,
    value: string[],
    onChange: (guids: string[]) => void
  ) => (
    <Autocomplete<PharmacyOption, true>
      multiple size="small" limitTags={1} disableCloseOnSelect
      options={meta.pharmacies}
      value={toOptions(value)}
      onChange={(_, v) => onChange(v.map(o => o.guid))}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(a, b) => a.guid === b.guid}
      groupBy={o => (o.kind === 'warehouse' ? 'Склады и офисы' : 'Аптеки')}
      noOptionsText="Ничего не найдено"
      renderOption={(props, o) => (
        <li {...props} key={o.guid}>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 1 }}>
            <span>{o.name}</span>
            {o.region && <Typography component="span" variant="caption" color="text.secondary">{o.region}</Typography>}
          </Box>
        </li>
      )}
      renderInput={params => <TextField {...params} label={label} sx={fieldSx} />}
    />
  );

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {/* Поиск и быстрые переключатели */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small" value={filters.search}
          onChange={e => patch({ search: e.target.value })}
          onKeyDown={e => { if (e.key === 'Escape') patch({ search: '' }); }}
          placeholder="№ документа, аптека, автор, комментарий"
          sx={{ flex: '1 1 280px', minWidth: 240, ...fieldSx }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
            endAdornment: filters.search ? (
              <InputAdornment position="end">
                <ClearRoundedIcon fontSize="small" sx={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => patch({ search: '' })} />
              </InputAdornment>
            ) : undefined
          }}
        />
        <ToggleButtonGroup
          size="small" exclusive value={filters.type}
          onChange={(_, v) => patch({ type: (v ?? '') as DocumentFilters['type'] })}
          sx={{ height: 36, '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, borderColor: BORDER } }}
        >
          {TYPE_OPTIONS.map(o => <ToggleButton key={o.value || 'all'} value={o.value}>{o.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Badge color="primary" badgeContent={activeCount} invisible={!activeCount} sx={{ '& .MuiBadge-badge': { right: 6, top: 6 } }}>
          <Button
            size="small" variant={advanced ? 'contained' : 'outlined'} color={advanced ? 'primary' : 'inherit'} disableElevation
            onClick={() => setAdvanced(v => !v)}
            startIcon={<TuneRoundedIcon fontSize="small" />}
            endIcon={<ExpandMoreRoundedIcon fontSize="small" sx={{ transform: advanced ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            sx={{ textTransform: 'none', height: 36, borderColor: BORDER, color: advanced ? undefined : '#334155' }}
          >
            Фильтры
          </Button>
        </Badge>
        <SavedViewsMenu filters={filters} canSave={hasAnyFilter(filters)} onApply={replace} />
      </Box>

      {/* Быстрые фильтры со счётчиками */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
        {meta.groups.map(g => {
          const active = groupTokens.includes(g.id);
          const color = GROUP_COLORS[g.id] || '#64748b';
          const count = groupCount(g.statuses);
          return (
            <Chip
              key={g.id} size="small" clickable onClick={() => toggleGroup(g.id)}
              label={count === null ? g.name : `${g.name}: ${count.toLocaleString('ru-RU')}`}
              variant={active ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600, height: 28,
                borderColor: active ? color : BORDER, color: active ? '#fff' : '#334155', bgcolor: active ? color : '#fff',
                '&:hover': { bgcolor: active ? color : '#f1f5f9' }
              }}
            />
          );
        })}
        <Chip
          size="small" clickable onClick={() => patch({ warn: !filters.warn })}
          icon={<HourglassBottomRoundedIcon sx={{ fontSize: 16, color: filters.warn ? '#fff !important' : '#d97706 !important' }} />}
          label={facets ? `Зависшие: ${facets.stuck.toLocaleString('ru-RU')}` : 'Зависшие'}
          variant={filters.warn ? 'filled' : 'outlined'}
          sx={{
            fontWeight: 600, height: 28,
            borderColor: filters.warn ? '#d97706' : BORDER, color: filters.warn ? '#fff' : '#334155', bgcolor: filters.warn ? '#d97706' : '#fff',
            '&:hover': { bgcolor: filters.warn ? '#d97706' : '#fffbeb' }
          }}
        />
        {([{ value: 'marked', label: 'С маркировкой' }, { value: 'unmarked', label: 'Без маркировки' }] as const).map(m => {
          const active = filters.marking === m.value;
          return (
            <Chip
              key={m.value} size="small" clickable onClick={() => patch({ marking: active ? '' : m.value })} label={m.label}
              variant={active ? 'filled' : 'outlined'} color={active ? 'primary' : 'default'}
              sx={{ height: 28, fontWeight: 600, borderColor: active ? undefined : BORDER, color: active ? undefined : '#334155', bgcolor: active ? undefined : '#fff' }}
            />
          );
        })}
        <Box sx={{ width: '1px', height: 20, bgcolor: BORDER, mx: 0.5 }} />
        {[{ days: 0, label: 'Сегодня' }, { days: 7, label: '7 дней' }, { days: 30, label: '30 дней' }].map(r => (
          <Chip
            key={r.days} size="small" clickable onClick={() => applyRange(r.days)} label={r.label}
            variant={rangePreset(r.days) ? 'filled' : 'outlined'}
            color={rangePreset(r.days) ? 'primary' : 'default'}
            sx={{ height: 28, fontWeight: 500, borderColor: rangePreset(r.days) ? undefined : BORDER, bgcolor: rangePreset(r.days) ? undefined : '#fff' }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        {hasAnyFilter(filters) && (
          <Button size="small" color="inherit" onClick={reset} sx={{ textTransform: 'none', color: '#64748b' }}>
            Сбросить всё
          </Button>
        )}
      </Box>

      {/* Расширенные фильтры */}
      <Collapse in={advanced} unmountOnExit>
        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', pt: 0.5 }}>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}>
            <ProductFilter
              text={filters.product} products={filters.products}
              onChange={v => patch({ product: v.text, products: v.products })}
            />
          </Box>
          {pharmacyAutocomplete('Участник (откуда или куда)', filters.pharmacy, v => patch({ pharmacy: v }))}
          {pharmacyAutocomplete('Отправитель', filters.sender, v => patch({ sender: v }))}
          {pharmacyAutocomplete('Получатель', filters.recipient, v => patch({ recipient: v }))}

          <Autocomplete<string, true>
            multiple size="small" limitTags={2} disableCloseOnSelect
            options={meta.regions} value={filters.region}
            onChange={(_, v) => patch({ region: v })}
            noOptionsText="Ничего не найдено"
            renderInput={params => <TextField {...params} label="Регион" sx={fieldSx} />}
          />

          <Autocomplete<{ id: string; name: string; group: string }, true>
            multiple size="small" limitTags={1} disableCloseOnSelect
            options={statusOptions}
            value={statusOptions.filter(o => numericStatuses.includes(o.id))}
            onChange={(_, v) => patch({ statuses: [...groupTokens, ...v.map(o => o.id)] })}
            getOptionLabel={o => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            groupBy={o => o.group}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 1 }}>
                  <span>{o.name}</span>
                  {facets && <Typography component="span" variant="caption" color="text.secondary">{(facets.status[o.id] || 0).toLocaleString('ru-RU')}</Typography>}
                </Box>
              </li>
            )}
            renderInput={params => <TextField {...params} label="Статус (детально)" sx={fieldSx} />}
          />

          <TextField select size="small" label="Маршрут" value={filters.route} onChange={e => patch({ route: e.target.value as DocumentFilters['route'] })} sx={fieldSx}>
            <MenuItem value="">Любой</MenuItem>
            <MenuItem value="direct">Прямое перемещение</MenuItem>
            <MenuItem value="via">Через склад (РЦ)</MenuItem>
          </TextField>

          <TextField
            select size="small" label="Маркировка" value={filters.marking}
            onChange={e => patch({ marking: e.target.value as DocumentFilters['marking'] })} sx={fieldSx}
            helperText="Признак есть только у возвратов"
          >
            <MenuItem value="">Любая</MenuItem>
            <MenuItem value="marked">С маркировкой (КМ)</MenuItem>
            <MenuItem value="unmarked">Без маркировки</MenuItem>
          </TextField>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" type="date" label="Создан с" value={filters.createdFrom} onChange={e => patch({ createdFrom: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1, ...fieldSx }} />
            <TextField size="small" type="date" label="по" value={filters.createdTo} onChange={e => patch({ createdTo: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1, ...fieldSx }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" type="date" label="Обновлён с" value={filters.updatedFrom} onChange={e => patch({ updatedFrom: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1, ...fieldSx }} />
            <TextField size="small" type="date" label="по" value={filters.updatedTo} onChange={e => patch({ updatedTo: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1, ...fieldSx }} />
          </Box>

          <TextField
            size="small" type="number" label="Без изменений, дней" value={filters.stuckDays}
            onChange={e => patch({ stuckDays: e.target.value.replace(/\D/g, '').slice(0, 3) })}
            inputProps={{ min: 1, max: 365 }} sx={fieldSx}
            helperText="Среди документов на контроле автоочистки"
          />
          <TextField size="small" label="Автор" value={filters.author} onChange={e => patch({ author: e.target.value })} sx={fieldSx} />
          <TextField select size="small" label="Скрытые документы" value={filters.hidden} onChange={e => patch({ hidden: e.target.value as DocumentFilters['hidden'] })} sx={fieldSx}>
            <MenuItem value="all">Показывать все</MenuItem>
            <MenuItem value="exclude">Не показывать скрытые</MenuItem>
            <MenuItem value="only">Только скрытые</MenuItem>
          </TextField>
        </Box>
      </Collapse>

      {activeChips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {activeChips.map(c => (
            <Chip key={c.key} size="small" label={c.label} onDelete={c.clear} sx={{ bgcolor: '#f1f5f9', color: '#334155', maxWidth: 360 }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DocumentsFilters;
